<app-mobile-header-secondary title="{{ 'profile.title' | translate }}" [standalone]="standalone"></app-mobile-header-secondary>

@if (currentUser) {
  <article class="configuration-item">
    <div class="configuration__header">
      <div>
        <div class="configuration-title">{{ 'profile.personalInfo' | translate }}</div>
        <div class="configuration-text">{{ 'settings.personalInfoData' | translate }}</div>

        @if (urlParamsService.params.documentationLink && !enterpriseService.hideForEnterprise()) {
          <a [href]="helpLink" target="_blank" class="btn-S btn-text icon-start mt-1 text-decoration-none">
            <i class="far fa-question-circle"></i>
            {{ 'settings.screenHelp' | translate }}
          </a>
        }
      </div>

      <button type="button" class="btn-M btn-text" (click)="openEditProfileModal()" [disabled]="isInitialUserLoading">{{ 'button.edit' | translate }}</button>
    </div>

    <div class="configuration-column__container">
      <div class="configuration-column">
        <div class="configuration-subtitle">{{ 'profile.' + (currentUser.company || currentUser.cnpj ? 'companyData' : 'personalData') | translate }}</div>

        @if (currentUser.company) {
          <label class="field__holder" [loadingAnimation]="isInitialUserLoading">
            <input type="text" class="field" [value]="currentUser.company" disabled />
            <span class="field__placeholder">{{ 'profile.businessName' | translate }}</span>
          </label>
        }

        @if ((currentUser.company || currentUser.cnpj) && (currentUser?.locale?.country === 'BR' || currentUser?.locale?.country === null)) {
          <label class="field__holder" [loadingAnimation]="isInitialUserLoading">
            <input type="text" class="field" [value]="currentUser.cnpj" disabled />
            <span class="field__placeholder">{{ 'profile.cnpj' | translate }}</span>
          </label>
        }

        @if (currentUser.company) {
          <div class="configuration-subtitle">{{ 'profile.personalData' | translate }}</div>
        }

        <label class="field__holder" [loadingAnimation]="isInitialUserLoading">
          <input type="text" class="field" [value]="currentUser.name" disabled />
          <span class="field__placeholder">{{ 'profile.fullName' | translate }}</span>
        </label>

        <div class="configuration-row">
          @if (currentUser.birthday) {
            <div class="configuration-row--half">
              <label class="field__holder" [loadingAnimation]="isInitialUserLoading">
                <input type="text" class="field" [value]="dateService.normalizeDate(currentUser.birthday)" disabled />
                <span class="field__placeholder">{{ 'profile.birthDate' | translate }}</span>
              </label>
            </div>
          }

          @if (currentUser.cpf && (currentUser?.locale?.country === 'BR' || currentUser?.locale?.country === null)) {
            <div class="configuration-row--half">
              <label class="field__holder" [loadingAnimation]="isInitialUserLoading">
                <input type="text" class="field" [value]="currentUser.cpf" disabled />
                <span class="field__placeholder">{{ 'profile.cpf' | translate }}</span>
              </label>
            </div>
          }
        </div>
      </div>

      @if (!isMobile) {
        <div class="configuration-column">
          <div class="configuration-preview-box">
            <p class="configuration-preview-text">{{ 'settings.howItAppearsInDocument' | translate }}</p>

            @if (fakeSigner && !isInitialUserLoading) {
              <app-signer-card [signer]="fakeSigner" [documentAuthorId]="currentUser.id"></app-signer-card>
            }
          </div>
        </div>
      }
    </div>
  </article>

  <article class="configuration-item">
    <div class="configuration__header">
      <div>
        <div class="configuration-title">{{ 'settings.signatureAppearance' | translate }}</div>
        <div class="configuration-text">{{ 'settings.signatureAppearanceDesc' | translate }}</div>
      </div>

      <div>
        @if (isEditingSignature) {
          <button type="button" class="btn-M btn-filled-cancel mr-2" (click)="isEditingSignature = false">{{ 'button.cancel' | translate }}</button>
          <button type="button" class="btn-M btn-primary" (click)="updateSignature()" [disabled]="signatureForm.invalid || isLoading || !canSubmitForm">{{ 'button.save' | translate }}</button>
        } @else {
          <button type="button" class="btn-M btn-text" (click)="isMobile ? openEditSignatureModalForMobile() : (isEditingSignature = true)" [disabled]="isInitialUserLoading">
            {{ 'button.edit' | translate }}
          </button>
        }
      </div>
    </div>

    <div class="configuration-column__container">
      <div class="configuration-column">
        @if (isEditingSignature) {
          <form class="form d-none d-lg-block" [formGroup]="signatureForm" novalidate>
            <app-signature-pad
              [isLoading]="isLoading"
              [font]="signatureForm.get('signature.font').value"
              [format]="signatureForm.get('signature.format').value"
              (formatChange)="signatureForm.get('signature.format').setValue($event); signatureForm.get('initials.format').setValue($event)"
              (signatureTextChange)="signatureForm.get('signature.text').setValue($event)"
              (initialsTextChange)="signatureForm.get('initials.text').setValue($event)"
              (fontChange)="signatureForm.get('signature.font').setValue($event); signatureForm.get('initials.font').setValue($event)"
              (canSubmitForm)="canSubmitForm = $event"
              #signaturePad
            >
            </app-signature-pad>
          </form>
        } @else {
          <div class="configuration-row configuration-row--preview">
            <app-signature-preview
              [user]="currentUser"
              [type]="PositionElementEnum.Signature"
              [font]="currentUser.settings.font"
              [format]="currentUser.settings.format"
              [text]="currentUser.settings.signature_text"
              [imageUrl]="currentUser.settings.signature"
              [drawingUrl]="currentUser.settings.signature_draw"
              [loadingAnimation]="isInitialUserLoading"
            >
            </app-signature-preview>

            <app-signature-preview
              [user]="currentUser"
              [type]="PositionElementEnum.Initials"
              [font]="currentUser.settings.font"
              [format]="currentUser.settings.initials_format"
              [text]="currentUser.settings.initials_text"
              [imageUrl]="currentUser.settings.initials"
              [drawingUrl]="currentUser.settings.initials_draw"
              [loadingAnimation]="isInitialUserLoading"
            >
            </app-signature-preview>
          </div>
        }
      </div>
    </div>
  </article>

  <article class="configuration-item">
    <div class="configuration__header">
      <div class="configuration-title">{{ 'settings.languageLocation' | translate }}</div>
      <button type="button" class="btn-M btn-text" (click)="openEditLocationModal()" [disabled]="isInitialUserLoading">{{ 'button.edit' | translate }}</button>
    </div>

    <div class="configuration-column__container">
      <div class="configuration-column mt-0">
        <div class="configuration-row">
          <div class="configuration-row--half">
            <label class="field__holder" [loadingAnimation]="isInitialUserLoading">
              @if (!!currentCountryIcon) {
                <i [class]="currentCountryIcon"></i>
              }
              <input type="text" class="field" [value]="i18nService.getSelectDisplayValue(countries, currentUser.locale?.country)" disabled />
              <span class="field__placeholder">{{ 'profile.country' | translate }}</span>
            </label>
          </div>

          <div class="configuration-row--half">
            <label class="field__holder" [loadingAnimation]="isInitialUserLoading">
              <input type="text" class="field" [value]="i18nService.getSelectDisplayValue(languages, currentUser.locale?.language)" disabled />
              <span class="field__placeholder">{{ 'profile.language' | translate }}</span>
            </label>
          </div>
        </div>

        <div class="configuration-row">
          <div class="configuration-row--half">
            <label class="field__holder" [loadingAnimation]="isInitialUserLoading">
              <input type="text" class="field" [value]="i18nService.getSelectDisplayValue(timezones, currentUser.locale?.timezone)" disabled />
              <span class="field__placeholder">{{ 'profile.timezone' | translate }}</span>
            </label>
          </div>

          <div class="configuration-row--half">
            <label class="field__holder" [loadingAnimation]="isInitialUserLoading">
              <input type="text" class="field" [value]="i18nService.getSelectDisplayValue(dateFormatOptions, currentUser.locale?.date_format)" disabled />
              <span class="field__placeholder">{{ 'profile.dateFormat' | translate }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </article>
}
